import api from 'api';
import {
  EarningsComponentType,
  NonPayableBenefitsComponentType,
  SalaryComponent,
} from 'components/SalaryComponents/types';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { useQuery } from 'react-query';
import { convertToStructureComponent } from '../utils';

export const useSalaryStructureComponents = () => {
  // Fetch earning components
  const {
    data: earningsData,
    isLoading: isEarningsLoading,
    error: earningsError,
  } = useQuery(['salaryComponents', 'earnings'], () =>
    api.salaryComponents.queries.listSalaryComponents<SalaryComponent>({
      category: 'EARNINGS_CATEGORY',
      status: 'COMPONENT_STATUS_ACTIVE',
    }),
  );

  // Fetch non-payable benefits components
  const {
    data: nonPayableBenefitsData,
    isLoading: isNonPayableBenefitsLoading,
    error: nonPayableBenefitsError,
  } = useQuery(['salaryComponents', 'nonPayableBenefits'], () =>
    api.salaryComponents.queries.listSalaryComponents<SalaryComponent>({
      category: 'NON_PAYABLE_BENEFITS_CATEGORY',
      status: 'COMPONENT_STATUS_ACTIVE',
    }),
  );

  // Process and categorize components
  const earningComponents = (earningsData?.payrollListSalaryComponents?.data?.salaryComponents ||
    []) as EarningsComponentType[];

  const nonPayableBenefitComponents = (nonPayableBenefitsData?.payrollListSalaryComponents?.data
    ?.salaryComponents || []) as NonPayableBenefitsComponentType[];

  // Categorize earnings into fixed and variable
  const fixedEarningComponents = earningComponents.filter(
    (component) => component.settings?.earningDetails.payType === 'fixed',
  );

  const variableEarningComponents = earningComponents.filter(
    (component) => component.settings?.earningDetails?.payType === 'variable',
  );

  // Convert to structure components format
  const fixedEarningStructureComponents: SalaryStructureComponent[] = fixedEarningComponents.map(
    (component) => convertToStructureComponent(component),
  );

  const variableEarningStructureComponents: SalaryStructureComponent[] =
    variableEarningComponents.map((component) => convertToStructureComponent(component));

  const nonPayableBenefitStructureComponents: SalaryStructureComponent[] =
    nonPayableBenefitComponents.map((component) => convertToStructureComponent(component));

  const allComponents = [...earningComponents, ...nonPayableBenefitComponents];

  const isLoading = isEarningsLoading || isNonPayableBenefitsLoading;
  const error = earningsError || nonPayableBenefitsError;

  return {
    allComponents,
    earningComponents,
    nonPayableBenefitComponents,
    fixedEarningComponents,
    variableEarningComponents,
    fixedEarningStructureComponents,
    variableEarningStructureComponents,
    nonPayableBenefitStructureComponents,
    isLoading,
    error,
  };
};
